import { useState } from "react";
import { Check, RotateCcw, Send, Trash2, Clock, CheckCircle2, Paperclip, Pencil, FileText, Download, Repeat, CalendarClock, CalendarPlus, Sparkles, Hand, AlertTriangle, MoreHorizontal, Users, Circle, Siren, ClipboardList, ChevronLeft, Loader2, Mail } from "lucide-react";
import { Pill, BrutalButton } from "@/components/Brutal";
import { DatePicker } from "@/components/DatePicker";
import { PersonMultiSelect, GroupMultiSelect } from "@/components/Selects";
import { dueMeta, taskOverdueInfo, userColor, taskEffDate, weekdayDayLabel, localISO } from "@/lib/labels";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";
import { useConfirm } from "@/components/ConfirmProvider";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { fireConfetti } from "@/components/Confetti";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function attUrl(att) {
  const token = localStorage.getItem("doenies_token");
  const sep = att.url.includes("?") ? "&" : "?";
  return `${BACKEND}${att.url}${token ? `${sep}auth=${token}` : ""}`;
}

export default function TaskCard({ task, users = [], groups = [], onChange, onEdit, batch = null }) {
  const confirm = useConfirm();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const t = useT();
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferBusy, setTransferBusy] = useState(null);
  const [swapOpen, setSwapOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [completeChooserOpen, setCompleteChooserOpen] = useState(false);
  const [behalfUid, setBehalfUid] = useState("");
  const [reassignMode, setReassignMode] = useState(false);
  const [reassignDoer, setReassignDoer] = useState("");
  const [reassignBusy, setReassignBusy] = useState(false);
  const [busyWho, setBusyWho] = useState(null);
  const [takeChoiceOpen, setTakeChoiceOpen] = useState(false);
  const [takeAssignee, setTakeAssignee] = useState("");
  const [takeNote, setTakeNote] = useState("");
  const [takeBusy, setTakeBusy] = useState(false);
  const [noteEditOpen, setNoteEditOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [weekChoice, setWeekChoice] = useState(null);
  const [postponeOpen, setPostponeOpen] = useState(false);
  const [pPenalty, setPPenalty] = useState(true);
  const [restoreChooserOpen, setRestoreChooserOpen] = useState(false);
  const [pPreview, setPPreview] = useState(null);
  const tomorrow = new Date(Date.now() + 864e5).toISOString().slice(0, 10);
  const [pDate, setPDate] = useState(tomorrow);
  const [pReason, setPReason] = useState("");
  const [viewer, setViewer] = useState(false);
  const [swapPersons, setSwapPersons] = useState([]);
  const [swapGroups, setSwapGroups] = useState([]);
  const meta = dueMeta(task.due_when);
  const Icon = meta.icon;
  const assigneeIds = task.assignee_ids?.length ? task.assignee_ids : (task.assignee_id ? [task.assignee_id] : []);
  const assignees = assigneeIds.map((id) => users.find((u) => u.user_id === id)).filter(Boolean);
  const creator = task.created_by ? users.find((u) => u.user_id === task.created_by) : null;
  const creatorName = creator?.name || task.created_by_name || null;
  const done = task.status === "klaar";
  const waiting = task.status === "wacht_op_bevestiging";
  const settled = done || waiting;

  const effDate = taskEffDate(task);
  const { overdue, daysOver } = taskOverdueInfo(task);
  const isPostponed = !!task.postponed_at && !settled;
  const postponedDate = () => { try { return new Date((task.original_date || effDate) + "T00:00:00").toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" }); } catch { return ""; } };
  const whenLabel = () => {
    const lbl = weekdayDayLabel(effDate);
    if (daysOver === 1) return `${t("Gisteren")} · ${lbl}`;
    if (daysOver === 2) return `${t("Eergisteren")} · ${lbl}`;
    return lbl;
  };

  const act = async (fn, label) => {
    try { await fn(); onChange?.(); if (label) toast.success(label); }
    catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };

  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const takeTask = async () => {
    try {
      const { data } = await api.post(`/tasks/${task.id}/take`, {});
      if (data?.needs_choice) { setTakeAssignee(""); setTakeNote(""); setTakeChoiceOpen(true); return; }
      fireConfetti();
      const pts = data?.points_awarded;
      toast.success(pts ? `🎉 ${t("Jij pakt dit op!")} +${pts} ${t("punten")}` : t("Jij pakt dit op! 💪"));
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };
  const submitTakeChoice = async (payload) => {
    if (takeBusy) return;
    setTakeBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/take`, payload);
      setTakeChoiceOpen(false);
      if (payload.confirm_take_self) {
        fireConfetti();
        const pts = data?.points_awarded;
        toast.success(pts ? `🎉 ${t("Jij pakt dit op!")} +${pts} ${t("punten")}` : t("Jij pakt dit op! 💪"));
      } else {
        const name = users.find((u) => u.user_id === payload.assign_to)?.name;
        toast.success(`📣 ${t("Toegewezen aan")} ${name || t("iemand")} — ${t("SMS verstuurd")}`, { duration: 5000 });
      }
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
    finally { setTakeBusy(false); }
  };
  const completeSelf = async () => {
    if (busyWho) return;
    setBusyWho("self");
    try {
      const { data } = await api.post(`/tasks/${task.id}/complete`);
      setCompleteChooserOpen(false);
      fireConfetti();
      const pts = data?.points_awarded;
      toast.success(pts ? `🎉 ${t("Taak afgerond!")} +${pts} ${t("punten")}` : t("Taak afgerond! 🎉"));
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
    finally { setBusyWho(null); }
  };
  const onCompleteClick = () => {
    if (settled) { act(() => api.post(`/tasks/${task.id}/reopen`)); return; }
    if (admin) { setBehalfUid(""); setReassignMode(false); setReassignDoer(""); setCompleteChooserOpen(true); }
    else { completeSelf(); }
  };
  const aiAssign = async () => {
    if (aiBusy) return;
    setAiBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/ai-assign`);
      setAiResult({ name: data.assignee_name, reason: data.reason });
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || t("Toewijzen mislukt")); }
    finally { setAiBusy(false); }
  };

  const [nudgeBusy, setNudgeBusy] = useState(false);
  const nudgeTask = async () => {
    if (nudgeBusy) return;
    if (!(await confirm({ title: t("Iedereen nú waarschuwen?"), description: t("Er gaat direct een push, e-mail én SMS uit dat deze taak niet langer kan wachten."), confirmLabel: t("Ja, stuur NU!"), tone: "info" }))) return;
    setNudgeBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/nudge`);
      const parts = [];
      if (data.push) parts.push(`📲 ${data.push} push`);
      if (data.email) parts.push(`✉️ ${data.email} e-mail`);
      parts.push(`💬 ${data.sms || 0} SMS`);
      toast.success(`🚨 ${t("NU! verstuurd naar")} ${data.recipients} ${t("perso(o)n(en)")} · ${parts.join(" · ")}`);
      if ((data.sms || 0) === 0) toast.warning(t("Geen SMS verstuurd — controleer of er telefoonnummers zijn ingevuld en of SMS is ingesteld."));
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
    finally { setNudgeBusy(false); }
  };

  const restorePerson = async (uid, name) => {
    if (!(await confirm({
      title: `${t("Terugzetten voor")} ${name}?`,
      description: t("Alleen deze persoon gaat terug naar open, de punten worden teruggedraaid en hij/zij krijgt direct een SMS-herinnering (met terugval op push/e-mail). De rest blijft klaar."),
      confirmLabel: t("Ja, terugzetten"),
    }))) return;
    act(() => api.post(`/tasks/${task.id}/restore-person`, { user_id: uid }), `${t("Teruggezet voor")} ${name} · ${t("herinnering verstuurd")}`);
  };

  const fmtWeekRange = (r) => {
    if (!r?.start || !r?.end) return "";
    const f = (d) => new Date(d + "T00:00:00").toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
    return `${f(r.start)} – ${f(r.end)}`;
  };

  const restorePersonDirect = (uid, name) => {
    setRestoreChooserOpen(false);
    act(() => api.post(`/tasks/${task.id}/restore-person`, { user_id: uid }), `${t("Teruggezet voor")} ${name} · ${t("herinnering verstuurd")}`);
  };

  const restoreAll = () => {
    setRestoreChooserOpen(false);
    act(() => api.post(`/tasks/${task.id}/restore`), t("Taak hersteld · punten teruggedraaid"));
  };

  const completeOnBehalf = async (uid, name, week) => {
    if (busyWho) return;
    setBusyWho(uid);
    try {
      const body = { on_behalf_of: uid };
      if (week) body.week = week;
      const { data } = await api.post(`/tasks/${task.id}/complete`, body);
      if (data?.needs_week_choice) { setCompleteChooserOpen(false); setWeekChoice({ uid, name, previous: data.previous_week, current: data.current_week }); return; }
      setCompleteChooserOpen(false);
      fireConfetti();
      const pts = data?.points_awarded;
      toast.success(pts ? `🎉 ${t("Afgevinkt namens")} ${name} · +${pts} ${t("punten")}` : `${t("Afgevinkt namens")} ${name} 🎉`);
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
    finally { setBusyWho(null); }
  };

  const completeReassign = async (doerId, doerName) => {
    if (reassignBusy) return;
    setReassignBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/complete`, { doer_id: doerId, neglected_ids: assigneeIds });
      setCompleteChooserOpen(false); setReassignMode(false);
      fireConfetti();
      const pts = data?.points_awarded;
      toast.success(pts ? `🎉 ${t("Afgevinkt · gedaan door")} ${doerName} · +${pts} ${t("punten")}` : `${t("Afgevinkt · gedaan door")} ${doerName} 🎉`);
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
    finally { setReassignBusy(false); }
  };

  const actions = [
    (!settled) && { key: "nudge", onClick: () => { setActionsOpen(false); nudgeTask(); }, icon: Siren, label: t("NU! — moet direct gebeuren"), color: "bg-red-500 text-white", disabled: nudgeBusy, testid: `task-nudge-${task.id}` },
    (!done && !task.is_quick_tile) && { key: "calendar", href: `${BACKEND}/api/calendar/task/${task.id}.ics`, icon: CalendarPlus, label: t("Zet in agenda"), color: "bg-brand-teal text-white", testid: `task-calendar-${task.id}` },
    (settled && admin) && { key: "restore", onClick: async () => { setActionsOpen(false); if (assignees.length > 1) { setRestoreChooserOpen(true); return; } if (await confirm({ title: t("Taak herstellen?"), description: t("De taak gaat terug naar open en de toegekende punten worden teruggedraaid. Gebruik dit als de taak niet (goed) is uitgevoerd."), confirmLabel: t("Ja, herstellen") })) act(() => api.post(`/tasks/${task.id}/restore`), t("Taak hersteld · punten teruggedraaid")); }, icon: RotateCcw, label: t("Herstellen (punten terug)"), color: "bg-brand-orange text-white", testid: `task-restore-${task.id}` },
    (waiting && admin) && { key: "confirm", onClick: () => { setActionsOpen(false); act(() => api.post(`/tasks/${task.id}/confirm`), t("Bevestigd")); }, icon: CheckCircle2, label: t("Bevestigen"), color: "bg-brand-green", testid: `task-confirm-${task.id}` },
    ...((admin && !settled) ? assignees.map((a) => ({ key: `behalf-${a.user_id}`, onClick: () => { setActionsOpen(false); completeOnBehalf(a.user_id, a.name); }, icon: Hand, label: `${t("Afvinken namens")} ${a.name}`, color: "bg-brand-green", testid: `task-complete-behalf-${task.id}-${a.user_id}` })) : []),
    (admin && !settled) && { key: "ai", onClick: () => { setActionsOpen(false); aiAssign(); }, icon: Sparkles, label: t("AI wijst toe"), color: "bg-brand-violet text-white", spin: aiBusy, disabled: aiBusy, testid: `task-ai-assign-${task.id}` },
    (task.transferable && !settled) && { key: "transfer", onClick: () => { setActionsOpen(false); setTransferOpen(true); }, icon: Send, label: t("Overdragen"), color: "bg-brand-blue text-white", testid: `task-transfer-${task.id}` },
    (task.transferable && !settled) && { key: "swap", onClick: () => { setActionsOpen(false); setSwapOpen(true); }, icon: Repeat, label: t("Uitvragen / ruilen"), color: "bg-brand-violet text-white", testid: `task-swap-${task.id}` },
    (!settled) && { key: "postpone", onClick: () => { setActionsOpen(false); setPDate(tomorrow); setPReason(""); setPPenalty(true); setPPreview(null); setPostponeOpen(true); api.get(`/tasks/${task.id}/postpone-preview`).then((r) => setPPreview(r.data)).catch(() => {}); }, icon: CalendarClock, label: t("Uitstellen"), color: "bg-brand-yellow", testid: `task-postpone-${task.id}` },
    (admin && !!task.assign_note) && { key: "edit-note", onClick: () => { setActionsOpen(false); setNoteDraft(task.assign_note || ""); setNoteEditOpen(true); }, icon: FileText, label: t("Briefje bewerken"), color: "bg-brand-cream", testid: `task-edit-note-${task.id}` },
    onEdit && { key: "edit", onClick: () => { setActionsOpen(false); onEdit(task); }, icon: Pencil, label: t("Bewerken"), color: "bg-brand-yellow", testid: `task-edit-${task.id}` },
    (task.recurrence && !task.one_time) && { key: "delete-series", onClick: async () => { setActionsOpen(false); if (await confirm({ title: t("Hele herhaalreeks verwijderen?"), description: t("Alle huidige én toekomstige herhalingen van deze taak worden verwijderd."), confirmLabel: t("Ja, hele reeks") })) act(() => api.delete(`/tasks/${task.id}?scope=series`), t("Herhaalreeks verwijderd")); }, icon: Trash2, label: t("Hele reeks verwijderen"), color: "bg-red-500 text-white", testid: `task-delete-series-${task.id}` },
    { key: "delete", onClick: async () => { setActionsOpen(false); if (!(await confirm({ title: task.recurrence && !task.one_time ? t("Alleen deze keer verwijderen?") : t("Taak verwijderen?"), description: task.recurrence && !task.one_time ? t("Alleen deze ene herhaling wordt verwijderd; de reeks blijft bestaan.") : "" }))) return; let revert = false; try { const { data } = await api.get(`/tasks/${task.id}/penalty-preview`); if (data.is_admin && data.count > 0 && data.points < 0) { revert = await confirm({ title: t("Strafpunten terugdraaien?"), description: `${t("Deze taak kreeg")} ${Math.abs(data.points)} ${t("strafpunten (te laat) toegekend. Wil je die terugdraaien bij het verwijderen?")}`, confirmLabel: t("Ja, terugdraaien"), cancelLabel: t("Nee, behouden") }); } } catch (e) {} act(() => api.delete(`/tasks/${task.id}${revert ? "?revert_penalty=true" : ""}`)); }, icon: Trash2, label: task.recurrence && !task.one_time ? t("Alleen deze keer verwijderen") : t("Verwijderen"), color: "bg-white", testid: `task-delete-${task.id}` },
  ].filter(Boolean);

  return (
    <div data-testid={`task-card-${task.id}`}
      className={`brutal rounded-xl p-4 transition-opacity ${isPostponed ? "bg-amber-50" : "bg-white"} ${settled ? "opacity-60" : ""}`}>
      <div className="flex items-start gap-3">
        <button data-testid={`task-complete-${task.id}`}
          onClick={onCompleteClick}
          className={`shrink-0 brutal-sm brutal-press rounded-lg w-9 h-9 flex items-center justify-center ${settled ? "bg-brand-green" : "bg-white"}`}>
          {settled ? <Check className="w-5 h-5" strokeWidth={3} /> : <span className="w-5 h-5 block" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {overdue ? (
              <>
                <Pill className="bg-red-500 text-white" data-testid={`task-overdue-${task.id}`}>
                  <AlertTriangle className="w-3 h-3" strokeWidth={3} /> {t("Te laat")}
                </Pill>
                <Pill className="bg-white text-brand-ink" data-testid={`task-overdue-when-${task.id}`}>
                  <CalendarClock className="w-3 h-3" strokeWidth={3} /> {whenLabel()}
                </Pill>
              </>
            ) : (isPostponed || task.due_when !== "vandaag" || !effDate || effDate === localISO(new Date())) && (
              <Pill className={`${meta.color} ${meta.text || "text-brand-ink"}`} data-testid={`task-when-${task.id}`}>
                <Icon className="w-3 h-3" strokeWidth={3} /> {isPostponed && effDate ? postponedDate() : (task.due_when === "specifiek" ? (task.due_time || t("Tijd")) : t(meta.label))}
              </Pill>
            )}
            {effDate && !overdue && !isPostponed && (
              <Pill className="bg-white text-brand-ink" data-testid={`task-date-label-${task.id}`}>
                <CalendarClock className="w-3 h-3" strokeWidth={3} /> {weekdayDayLabel(effDate)}
              </Pill>
            )}
            {task.is_quick_tile && <Pill className="bg-brand-orange text-white">{t("Quick")}</Pill>}
            {task.recurrence && <Pill className="bg-white"><RotateCcw className="w-3 h-3" strokeWidth={3} />{t("Herhaalt")}</Pill>}
            {waiting && <Pill className="bg-brand-yellow"><Clock className="w-3 h-3" strokeWidth={3} />{t("Bevestigen")}</Pill>}
            {task.postponed_at && !done && <Pill className="bg-brand-yellow"><CalendarClock className="w-3 h-3" strokeWidth={3} />{t("Uitgesteld")}{task.postpone_reason ? `: ${task.postpone_reason}` : ""}</Pill>}
            {task.auto_assigned && !done && <Pill className="bg-brand-violet text-white" data-testid={`task-auto-assigned-${task.id}`}><span>🤖</span>{t("Automatisch toegewezen")}</Pill>}
            {task.assign_note && !done && <Pill className="bg-brand-violet text-white" data-testid={`task-note-indicator-${task.id}`} title={task.assign_note}><Mail className="w-3 h-3" strokeWidth={3} />{t("Briefje")}</Pill>}
          </div>
          <h3 className={`font-head font-bold text-lg leading-tight ${settled ? "line-through" : ""}`}>{task.title}</h3>
          {task.description && <p className="text-sm text-brand-ink/60 mt-0.5">{task.description}</p>}
          {task.assign_note && !done && (
            <p data-testid={`task-assign-note-${task.id}`} className="text-xs font-semibold text-brand-ink/70 bg-brand-cream brutal-sm rounded-lg px-2.5 py-1 mt-1.5 inline-flex items-start gap-1.5">
              <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0 text-brand-violet" strokeWidth={2.5} />{t("Briefje")}: “{task.assign_note}”
            </p>
          )}
          {task.attachments?.length > 0 && (
            <button data-testid={`task-attachments-${task.id}`} onClick={() => setViewer(true)}
              className="flex items-center gap-1 text-xs text-brand-blue mt-1 font-bold underline decoration-2">
              <Paperclip className="w-3 h-3" strokeWidth={3} /> {task.attachments.length} {t("bijlage(n) bekijken")}
            </button>
          )}
          {batch && batch.length > 1 && (() => {
            const isDone = (bt) => ["klaar", "wacht_op_bevestiging"].includes(bt.status);
            const doneN = batch.filter(isDone).length;
            return (
              <div className="mt-2 rounded-xl bg-brand-cream brutal-sm p-2.5" data-testid={`task-batch-progress-${task.id}`}>
                <p className="text-xs font-black font-head mb-1.5 flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" strokeWidth={2.5} />{t("Per persoon")} — {doneN}/{batch.length} {t("klaar")}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {batch.map((bt) => {
                    const pid = bt.assignee_ids?.[0] || bt.assignee_id;
                    const p = users.find((u) => u.user_id === pid);
                    const dn = isDone(bt);
                    return (
                      <span key={bt.id} data-testid={`task-batch-member-${task.id}-${pid}`}
                        className={`text-xs font-bold rounded-full pl-1.5 pr-2.5 py-0.5 flex items-center gap-1 brutal-sm ${dn ? "bg-brand-green text-brand-ink" : "bg-white text-brand-ink/70"}`}>
                        <span className="w-3 h-3 rounded-full ring-1 ring-white shrink-0" style={{ background: userColor(pid).bg }} />
                        {dn ? <Check className="w-3 h-3" strokeWidth={3} /> : <Circle className="w-3 h-3" strokeWidth={2.5} />}
                        {p?.name || "?"}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {!batch && assignees.length > 1 && (() => {
            const doneIds = task.done_ids || [];
            const doneN = assignees.filter((a) => doneIds.includes(a.user_id)).length;
            return (
              <div className="mt-2 rounded-xl bg-brand-cream brutal-sm p-2.5" data-testid={`task-shared-progress-${task.id}`}>
                <p className="text-xs font-black font-head mb-1.5 flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" strokeWidth={2.5} />{t("Per persoon")} — {doneN}/{assignees.length} {t("klaar")}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {assignees.map((a) => {
                    const dn = doneIds.includes(a.user_id);
                    return (
                      <span key={a.user_id} data-testid={`task-shared-member-${task.id}-${a.user_id}`}
                        className={`text-xs font-bold rounded-full pl-1.5 pr-2 py-0.5 flex items-center gap-1 brutal-sm ${dn ? "bg-brand-green text-brand-ink" : "bg-white text-brand-ink/70"}`}>
                        <span className="w-3 h-3 rounded-full ring-1 ring-white shrink-0" style={{ background: userColor(a.user_id).bg }} />
                        {dn ? <Check className="w-3 h-3" strokeWidth={3} /> : <Circle className="w-3 h-3" strokeWidth={2.5} />}
                        {a.name}
                        {dn && admin && (
                          <button data-testid={`task-restore-person-${task.id}-${a.user_id}`}
                            onClick={() => restorePerson(a.user_id, a.name)} title={t("Terugzetten + herinnering")}
                            className="ml-0.5 rounded-full p-0.5 bg-brand-orange text-white brutal-press">
                            <RotateCcw className="w-3 h-3" strokeWidth={3} />
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          <div className="flex items-center gap-2 mt-3">
            {assignees.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {assignees.map((a) => { const c = userColor(a.user_id); return (
                  <span key={a.user_id} data-testid={`task-assignee-${task.id}-${a.user_id}`} className="text-xs font-bold bg-brand-cream brutal-sm rounded-full pl-1.5 pr-2.5 py-0.5 flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full ring-1 ring-white shrink-0" style={{ background: c.bg }} />{a.name}
                  </span>
                ); })}
              </div>
            ) : !settled ? (
              <div className="flex flex-wrap items-center gap-2">
                <BrutalButton data-testid={`task-take-${task.id}`} size="sm" variant="green"
                  onClick={takeTask}>
                  <Hand className="w-4 h-4" strokeWidth={2.5} />{t("Ikke!")}
                </BrutalButton>
                {admin && (
                  <BrutalButton data-testid={`task-ai-assign-inline-${task.id}`} size="sm" variant="violet"
                    disabled={aiBusy} onClick={aiAssign}>
                    <Sparkles className={`w-4 h-4 ${aiBusy ? "animate-pulse" : ""}`} strokeWidth={2.5} />{aiBusy ? t("Bezig…") : t("AI wijst toe")}
                  </BrutalButton>
                )}
              </div>
            ) : <span className="text-xs text-brand-ink/40 font-semibold">{t("Niemand toegewezen")}</span>}

            <div className="ml-auto">
              <button data-testid={`task-actions-${task.id}`} onClick={() => setActionsOpen(true)} title={t("Meer acties")}
                className="brutal-sm brutal-press rounded-full p-1.5 bg-white flex items-center justify-center"><MoreHorizontal className="w-4 h-4" strokeWidth={2.5} /></button>
            </div>
          </div>
          {creatorName && !task.is_quick_tile && (
            <p className="text-[11px] text-brand-ink/45 font-semibold mt-2 flex items-center gap-1" data-testid={`task-creator-${task.id}`}>
              <ClipboardList className="w-3 h-3" strokeWidth={2.5} /> {t("Opdracht van")} {creatorName}
            </p>
          )}
        </div>
      </div>

      <Dialog open={actionsOpen} onOpenChange={setActionsOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl" data-testid={`task-actions-menu-${task.id}`}>
          <DialogHeader><DialogTitle className="font-head font-black text-xl">{t("Acties")}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {actions.map((a) => a.href ? (
              <a key={a.key} href={a.href} data-testid={a.testid} onClick={() => setActionsOpen(false)}
                className="flex items-center gap-3 brutal-sm brutal-press rounded-xl px-4 py-3 bg-white font-bold">
                <span className={`rounded-full p-2 flex items-center justify-center ${a.color}`}><a.icon className="w-4 h-4" strokeWidth={2.5} /></span>{a.label}
              </a>
            ) : (
              <button key={a.key} data-testid={a.testid} onClick={a.onClick} disabled={a.disabled}
                className="w-full flex items-center gap-3 brutal-sm brutal-press rounded-xl px-4 py-3 bg-white font-bold text-left disabled:opacity-50">
                <span className={`rounded-full p-2 flex items-center justify-center ${a.color}`}><a.icon className={`w-4 h-4 ${a.spin ? "animate-pulse" : ""}`} strokeWidth={2.5} /></span>{a.label}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={noteEditOpen} onOpenChange={(v) => { if (!noteBusy) setNoteEditOpen(v); }}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl" data-testid={`note-edit-${task.id}`}>
          <DialogHeader><DialogTitle>{t("Briefje bewerken")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <textarea data-testid={`note-edit-input-${task.id}`} value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} rows={3}
              placeholder={t("Kort briefje voor de toegewezen persoon…")} className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none text-sm" />
            <div className="flex gap-2">
              <BrutalButton data-testid={`note-edit-clear-${task.id}`} disabled={noteBusy || !task.assign_note} onClick={async () => {
                setNoteBusy(true);
                try { await api.post(`/tasks/${task.id}/note`, { note: "" }); setNoteEditOpen(false); toast.success(t("Briefje verwijderd")); onChange?.(); }
                catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); } finally { setNoteBusy(false); }
              }} className="bg-white"><Trash2 className="w-4 h-4" strokeWidth={3} /> {t("Verwijderen")}</BrutalButton>
              <BrutalButton data-testid={`note-edit-save-${task.id}`} disabled={noteBusy} onClick={async () => {
                setNoteBusy(true);
                try { await api.post(`/tasks/${task.id}/note`, { note: noteDraft }); setNoteEditOpen(false); toast.success(t("Briefje opgeslagen")); onChange?.(); }
                catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); } finally { setNoteBusy(false); }
              }} className="flex-1 bg-brand-violet text-white">
                {noteBusy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} /> : <><Check className="w-4 h-4" strokeWidth={3} /> {t("Opslaan")}</>}
              </BrutalButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={takeChoiceOpen} onOpenChange={(v) => { if (!takeBusy) setTakeChoiceOpen(v); }}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl" data-testid={`take-chooser-${task.id}`}>
          <DialogHeader><DialogTitle>{t("Zelf overnemen of toewijzen?")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <BrutalButton data-testid={`take-self-${task.id}`} disabled={takeBusy} onClick={() => submitTakeChoice({ confirm_take_self: true })} className="w-full bg-brand-green">
              <Hand className="w-4 h-4" strokeWidth={3} /> {t("Ik neem 'm zelf over")}
            </BrutalButton>
            <div className="flex items-center gap-2 text-xs font-bold text-brand-ink/40"><div className="flex-1 h-px bg-brand-ink/15" />{t("of")}<div className="flex-1 h-px bg-brand-ink/15" /></div>
            <div className="space-y-2">
              <p className="text-sm font-bold">{t("Aan iemand anders toewijzen")}</p>
              <Select value={takeAssignee} onValueChange={setTakeAssignee}>
                <SelectTrigger data-testid={`take-assignee-select-${task.id}`} className="brutal-sm rounded-xl"><SelectValue placeholder={t("Kies een persoon…")} /></SelectTrigger>
                <SelectContent>
                  {users.filter((u) => !(u.role === "super_admin" || u.is_super_admin)).map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id} data-testid={`take-assignee-option-${u.user_id}`}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <textarea data-testid={`take-note-${task.id}`} value={takeNote} onChange={(e) => setTakeNote(e.target.value)} rows={2}
                placeholder={t("Kort briefje (gaat mee in SMS + melding)")} className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none text-sm" />
              <BrutalButton data-testid={`take-assign-confirm-${task.id}`} disabled={!takeAssignee || takeBusy} onClick={() => submitTakeChoice({ assign_to: takeAssignee, note: takeNote })} className="w-full bg-brand-violet text-white">
                {takeBusy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} /> : <><Send className="w-4 h-4" strokeWidth={3} /> {t("Toewijzen")}</>}
              </BrutalButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={completeChooserOpen} onOpenChange={setCompleteChooserOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl" data-testid={`complete-chooser-${task.id}`}>
          <DialogHeader><DialogTitle>{t("Wie heeft dit gedaan?")}</DialogTitle></DialogHeader>
          {!reassignMode ? (
          <div className="space-y-4">
            <BrutalButton data-testid={`complete-self-${task.id}`} disabled={!!busyWho} onClick={() => completeSelf()} className="w-full bg-brand-green">
              {busyWho === "self" ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} /> : <><Check className="w-4 h-4" strokeWidth={3} /> {t("Ikzelf")}</>}
            </BrutalButton>
            <div className="flex items-center gap-2 text-xs font-bold text-brand-ink/40">
              <div className="flex-1 h-px bg-brand-ink/10" />{t("of namens iemand anders")}<div className="flex-1 h-px bg-brand-ink/10" />
            </div>
            {(() => {
              const cands = users.filter((u) => u.user_id !== user?.user_id);
              if (cands.length === 0) return <p className="text-sm text-brand-ink/50 text-center">{t("Geen andere huisgenoten")}</p>;
              return cands.length > 6 ? (
                <div className="flex gap-2">
                  <Select value={behalfUid} onValueChange={setBehalfUid}>
                    <SelectTrigger data-testid={`complete-behalf-select-${task.id}`} className="flex-1"><SelectValue placeholder={t("Kies persoon")} /></SelectTrigger>
                    <SelectContent>
                      {cands.map((u) => <SelectItem key={u.user_id} value={u.user_id} data-testid={`complete-behalf-option-${u.user_id}`}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <BrutalButton disabled={!behalfUid || !!busyWho} data-testid={`complete-behalf-confirm-${task.id}`} onClick={() => { const u = users.find((x) => x.user_id === behalfUid); completeOnBehalf(behalfUid, u?.name); }} className="bg-brand-violet text-white">{busyWho === behalfUid && busyWho ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} /> : t("Afvinken")}</BrutalButton>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {cands.map((u) => (
                    <button key={u.user_id} data-testid={`complete-behalf-btn-${task.id}-${u.user_id}`} disabled={!!busyWho} onClick={() => completeOnBehalf(u.user_id, u.name)} className="brutal-sm brutal-press rounded-lg p-2 bg-white font-bold text-sm flex items-center gap-2 disabled:opacity-50">
                      {busyWho === u.user_id ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} /> : <Hand className="w-4 h-4" strokeWidth={3} />} <span className="truncate">{u.name}</span>
                    </button>
                  ))}
                </div>
              );
            })()}
            {assigneeIds.length > 0 && (
              <>
                <div className="flex items-center gap-2 text-xs font-bold text-brand-ink/40">
                  <div className="flex-1 h-px bg-brand-ink/10" />{t("of")}<div className="flex-1 h-px bg-brand-ink/10" />
                </div>
                <BrutalButton data-testid={`complete-reassign-open-${task.id}`} onClick={() => { setReassignDoer(""); setReassignMode(true); }} className="w-full bg-brand-orange text-white">
                  <Repeat className="w-4 h-4" strokeWidth={3} /> {t("Toegewezen persoon deed het niet")}
                </BrutalButton>
              </>
            )}
          </div>
          ) : (
          <div className="space-y-3" data-testid={`reassign-panel-${task.id}`}>
            <p className="text-sm text-brand-ink/60">{assignees.map((a) => a.name).join(", ")} {t("krijgt geen punten, wél strafpunten en een reflectiemail.")}</p>
            <p className="text-xs font-bold text-brand-ink/50">{t("Wie heeft de taak dan gedaan?")}</p>
            <div className="grid grid-cols-2 gap-2 max-h-[45vh] overflow-y-auto">
              {users.map((u) => {
                const on = reassignDoer === u.user_id;
                return (
                  <button key={u.user_id} data-testid={`reassign-option-${u.user_id}`} onClick={() => setReassignDoer(u.user_id)}
                    className={`brutal-sm brutal-press rounded-lg p-2.5 font-bold text-sm flex items-center gap-2 ${on ? "bg-brand-violet text-white" : "bg-white"}`}>
                    {on ? <Check className="w-4 h-4 shrink-0" strokeWidth={3} /> : <span className="w-4 h-4 shrink-0" />}<span className="truncate">{u.name}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              <BrutalButton data-testid={`reassign-back-${task.id}`} disabled={reassignBusy} onClick={() => setReassignMode(false)} className="bg-brand-cream"><ChevronLeft className="w-4 h-4" strokeWidth={3} /></BrutalButton>
              <BrutalButton disabled={!reassignDoer || reassignBusy} data-testid={`reassign-confirm-${task.id}`} onClick={() => { const u = users.find((x) => x.user_id === reassignDoer); completeReassign(reassignDoer, u?.name); }} className="flex-1 bg-brand-violet text-white">
                {reassignBusy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} /> : t("Afronden")}
              </BrutalButton>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>


      <Dialog open={!!weekChoice} onOpenChange={(v) => { if (!v) setWeekChoice(null); }}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl" data-testid={`week-choice-${task.id}`}>
          <DialogHeader><DialogTitle>{t("Voor welke week tellen de punten?")}</DialogTitle></DialogHeader>
          <p className="text-sm text-brand-ink/60">{t("De nieuwe week is net begonnen. Horen de punten van deze taak bij de vorige week of de huidige week?")}</p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <BrutalButton data-testid={`week-choice-prev-${task.id}`} onClick={() => { const w = weekChoice; setWeekChoice(null); completeOnBehalf(w.uid, w.name, "previous"); }} className="bg-brand-cream flex-col !py-2 h-auto">
              <span>{t("Vorige week")}</span>
              {weekChoice?.previous && <span className="text-xs font-semibold opacity-70 mt-0.5">{fmtWeekRange(weekChoice.previous)}</span>}
            </BrutalButton>
            <BrutalButton data-testid={`week-choice-current-${task.id}`} onClick={() => { const w = weekChoice; setWeekChoice(null); completeOnBehalf(w.uid, w.name, "current"); }} className="bg-brand-violet text-white flex-col !py-2 h-auto">
              <span>{t("Deze week")}</span>
              {weekChoice?.current && <span className="text-xs font-semibold opacity-80 mt-0.5">{fmtWeekRange(weekChoice.current)}</span>}
            </BrutalButton>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">{t("Overdragen aan")}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-72 overflow-auto no-scrollbar">
            {users.map((u) => (
              <button key={u.user_id} data-testid={`transfer-to-${u.user_id}`} disabled={!!transferBusy}
                onClick={async () => { if (transferBusy) return; setTransferBusy(u.user_id); try { await api.post(`/tasks/${task.id}/transfer`, { to_user_id: u.user_id }); setTransferOpen(false); onChange?.(); toast.success(`${t("Overgedragen aan")} ${u.name}`); } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); } finally { setTransferBusy(null); } }}
                className="w-full brutal-sm brutal-press rounded-xl px-4 py-3 bg-white text-left font-bold flex items-center justify-between disabled:opacity-50">
                {u.name} {transferBusy === u.user_id ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : <Send className="w-4 h-4" strokeWidth={2.5} />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={swapOpen} onOpenChange={setSwapOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">{t("Taak uitvragen / ruilen")}</DialogTitle></DialogHeader>
          <p className="text-sm text-brand-ink/60">{t("Vraag hulp met")} "{task.title}"{t(". Iemand kan 'm overnemen of een ruil voorstellen.")}</p>
          <button data-testid={`swap-broadcast-${task.id}`}
            onClick={() => act(async () => { await api.post(`/swaps`, { task_id: task.id }); setSwapOpen(false); }, t("Uitgevraagd aan iedereen 🔄"))}
            className="w-full brutal-sm brutal-press rounded-xl px-4 py-3 bg-brand-violet text-white text-left font-bold flex items-center justify-between">
            {t("Uitvragen aan iedereen")} <Repeat className="w-4 h-4" strokeWidth={2.5} />
          </button>

          <div className="border-t-2 border-dashed border-brand-ink/15 pt-3 space-y-3">
            <div>
              <p className="text-xs font-bold text-brand-ink/50 mb-1.5">{t("Of gericht aan groep(en):")}</p>
              <GroupMultiSelect groups={groups} value={swapGroups} onChange={setSwapGroups} testid="swap-groups" />
            </div>
            <div>
              <p className="text-xs font-bold text-brand-ink/50 mb-1.5">{t("En/of aan persoon/personen:")}</p>
              <PersonMultiSelect users={users.filter((u) => u.user_id !== task.assignee_id)} value={swapPersons} onChange={setSwapPersons} testid="swap-persons" />
            </div>
            <BrutalButton data-testid={`swap-send-targeted-${task.id}`} variant="dark" className="w-full"
              disabled={!swapPersons.length && !swapGroups.length}
              onClick={() => act(async () => {
                await api.post(`/swaps`, { task_id: task.id, direct_to_ids: swapPersons, target_group_ids: swapGroups });
                setSwapOpen(false); setSwapPersons([]); setSwapGroups([]);
              }, t("Gericht uitgevraagd 🔄"))}>
              <Repeat className="w-4 h-4" strokeWidth={2.5} />{t("Uitvragen aan selectie")}
            </BrutalButton>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={restoreChooserOpen} onOpenChange={setRestoreChooserOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl">
          <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2"><RotateCcw className="w-5 h-5" strokeWidth={2.5} />{t("Voor wie terugzetten?")}</DialogTitle></DialogHeader>
          <p className="text-sm text-brand-ink/60">{t("Deze taak is aan meerdere personen toegewezen. Kies wie 'm opnieuw moet doen — die persoon krijgt z'n punten teruggedraaid en direct een herinnering. De rest blijft klaar.")}</p>
          <div className="space-y-2">
            {assignees.map((a) => {
              const dn = (task.done_ids || []).includes(a.user_id);
              return (
                <button key={a.user_id} type="button" data-testid={`task-restore-choose-${task.id}-${a.user_id}`}
                  onClick={() => restorePersonDirect(a.user_id, a.name)}
                  className="w-full brutal-sm rounded-xl px-4 py-3 font-bold flex items-center gap-2 bg-white hover:bg-brand-cream transition-colors">
                  <span className="w-4 h-4 rounded-full ring-1 ring-white shrink-0" style={{ background: userColor(a.user_id).bg }} />
                  {a.name}
                  {!dn && <span className="text-xs font-semibold text-brand-ink/40">{t("(al open)")}</span>}
                  <RotateCcw className="w-4 h-4 ml-auto text-brand-orange" strokeWidth={3} />
                </button>
              );
            })}
          </div>
          <BrutalButton data-testid={`task-restore-all-${task.id}`} variant="orange" className="w-full" onClick={restoreAll}>
            <RotateCcw className="w-4 h-4" strokeWidth={2.5} />{t("Iedereen herstellen")}
          </BrutalButton>
        </DialogContent>
      </Dialog>

      <Dialog open={postponeOpen} onOpenChange={setPostponeOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl">
          <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2"><CalendarClock className="w-5 h-5" strokeWidth={2.5} />{t("Taak uitstellen")}</DialogTitle></DialogHeader>
          <p className="text-sm text-brand-ink/60">{t("Verplaats")} "{task.title}" {t("naar een andere dag. Geef eventueel een reden op (\"kan vandaag niet, wel later\").")}</p>
          <div>
            <label className="text-sm font-bold">{t("Nieuwe datum")}</label>
            <DatePicker testid={`postpone-date-${task.id}`} value={pDate} min={new Date().toISOString().slice(0, 10)} onChange={(v) => setPDate(v)} className="mt-1" />
          </div>
          <div>
            <label className="text-sm font-bold">{t("Reden (optioneel)")}</label>
            <input data-testid={`postpone-reason-${task.id}`} value={pReason} onChange={(e) => setPReason(e.target.value)}
              placeholder={t("Bijv. werk uitgelopen")} className="w-full brutal-sm rounded-xl px-4 py-2.5 font-medium outline-none mt-1" />
          </div>
          {pPreview?.rewards_active && pPreview?.penalty ? (
            admin ? (
              <div className="brutal-sm rounded-lg bg-brand-yellow/30 p-2.5" data-testid={`postpone-penalty-choice-${task.id}`}>
                <p className="text-sm font-bold text-brand-ink flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 shrink-0" strokeWidth={3} />
                  {t("Kost dit uitstellen strafpunten?")} ({Math.abs(pPreview.penalty)} {t("punten")})
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" data-testid={`postpone-penalty-yes-${task.id}`} onClick={() => setPPenalty(true)}
                    className={`brutal-sm rounded-lg px-3 py-2 text-sm font-black transition-colors ${pPenalty ? "bg-brand-orange text-white" : "bg-white text-brand-ink/70"}`}>
                    {t("Ja, met strafpunten")}
                  </button>
                  <button type="button" data-testid={`postpone-penalty-no-${task.id}`} onClick={() => setPPenalty(false)}
                    className={`brutal-sm rounded-lg px-3 py-2 text-sm font-black transition-colors ${!pPenalty ? "bg-brand-green text-brand-ink" : "bg-white text-brand-ink/70"}`}>
                    {t("Nee, zonder straf")}
                  </button>
                </div>
              </div>
            ) : (
              <div data-testid={`postpone-penalty-warning-${task.id}`} className="brutal-sm rounded-lg bg-brand-yellow/40 p-2.5 text-sm font-bold text-brand-ink flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" strokeWidth={3} />
                <span>{pPreview.times_postponed === 1
                  ? `${t("Let op: uitstellen kost deze keer")} ${Math.abs(pPreview.penalty)} ${t("punten")}.`
                  : `${t("Dit is de")} ${pPreview.times_postponed}e ${t("keer uitstellen — dat kost")} ${Math.abs(pPreview.penalty)} ${t("punten")}.`}</span>
              </div>
            )
          ) : null}
          <BrutalButton data-testid={`postpone-submit-${task.id}`} className="w-full" disabled={!pDate}
            onClick={() => act(async () => { await api.post(`/tasks/${task.id}/postpone`, { new_date: pDate, reason: pReason, apply_penalty: pPenalty }); setPostponeOpen(false); }, t("Taak uitgesteld 📆"))}>
            {t("Uitstellen")}
          </BrutalButton>
        </DialogContent>
      </Dialog>

      <Dialog open={!!aiResult} onOpenChange={(v) => { if (!v) setAiResult(null); }}>
        <DialogContent data-testid={`ai-result-${task.id}`} className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[90vh] overflow-auto no-scrollbar">
          <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-brand-violet" strokeWidth={2.5} />{t("Eerlijk toegewezen")}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-center py-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-brand-violet text-white flex items-center justify-center font-head font-black text-2xl">{(aiResult?.name || "?").charAt(0)}</div>
            <p className="font-head font-black text-2xl">{aiResult?.name}</p>
            <p className="text-sm font-semibold text-brand-ink/70 brutal-sm rounded-xl bg-brand-violet/10 px-4 py-3">{aiResult?.reason}</p>
            <BrutalButton data-testid={`ai-result-close-${task.id}`} className="w-full" onClick={() => setAiResult(null)}>{t("Top, duidelijk!")}</BrutalButton>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={viewer} onOpenChange={setViewer}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[90vh] overflow-auto no-scrollbar">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">{t("Bijlagen")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {task.attachments?.map((att, i) => {
              const type = att.content_type || "";
              const src = attUrl(att);
              return (
                <div key={i} data-testid={`attachment-${task.id}-${i}`} className="brutal-sm rounded-xl overflow-hidden bg-brand-cream">
                  {type.startsWith("image/") && <img src={src} alt={att.filename} className="w-full object-contain max-h-72" />}
                  {type.startsWith("video/") && <video src={src} controls className="w-full max-h-72 bg-black" />}
                  {!type.startsWith("image/") && !type.startsWith("video/") && (
                    <a href={src} target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-3 font-bold">
                      <FileText className="w-5 h-5" strokeWidth={2.5} />
                      <span className="flex-1 truncate">{att.filename || t("Document")}</span>
                      <Download className="w-4 h-4" strokeWidth={2.5} />
                    </a>
                  )}
                  {(type.startsWith("image/") || type.startsWith("video/")) && (
                    <div className="px-3 py-1.5 text-xs font-semibold text-brand-ink/60 truncate">{att.filename}</div>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
